import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ReactComponent as LogoSvg } from '../../../assets/marimosca-main-logo.svg';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbContext';
import Icon from '../Icon/Icon';
import sidebarStyles from '../Sidebar/Sidebar.module.css';
import 'material-symbols/outlined.css';
import styles from './TopBar.module.css';

interface TopBarProps {
  isMobile?: boolean;
  onMenuToggle?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({ isMobile = false, onMenuToggle }) => {
  const { breadcrumbs } = useBreadcrumbs();
  const location = useLocation();

  /* Build the full crumb trail: Início + context breadcrumbs */
  const allCrumbs = [{ label: 'Home', path: '/' }, ...breadcrumbs];

  /* Derive a sensible default from the route when no breadcrumbs are set */
  const routeLabel: Record<string, string> = {
    '/': '',
    '/metas': 'Metas',
    '/a-vencer': 'A vencer/vencidos',
    '/recebimentos': 'Pagamentos',
    '/alunas': 'Alunas',
    '/sys': 'Sistema',
    '/sys/logs': 'Logs',
    '/sys/payment-errors': 'Erros de pagamento',
    '/pagamentos': 'Planos e pagamentos',
    '/orquestrador': 'Orquestrador',
    '/performance': 'Performance',
    '/gestao-entregas-treinos': 'Gestão de entregas de treinos',
    '/comercial': 'Comercial',
    '/comercial/declinadas': 'Declinadas',
    '/ajuda': 'Ajuda',
    '/acompanhamento-onboarding': 'Onboarding',
    '/exercicios': 'Exercícios',
    '/treinos': 'Treinos',
  };

  const effectiveCrumbs =
    breadcrumbs.length > 0
      ? allCrumbs
      : routeLabel[location.pathname]
        ? [{ label: 'Home', path: '/' }, { label: routeLabel[location.pathname], path: location.pathname }]
        : [{ label: 'Home', path: '/' }];

  return (
    <header className={styles.topbar}>
      {/* Left section — logo + breadcrumbs */}
      <div className={styles['topbar-left']}>
        {isMobile && onMenuToggle && (
          <button
            type="button"
            className={sidebarStyles['hamburger-btn']}
            onClick={onMenuToggle}
            aria-label="Abrir menu"
          >
            <Icon name="menu" size={22} />
          </button>
        )}

        <Link to="/" className={styles['topbar-logo-link']}>
          <LogoSvg className={styles['topbar-logo']} />
        </Link>

        <nav className={styles['breadcrumb-nav']} aria-label="Breadcrumb">
          <Link to="/" className={styles['home-icon-link']}>
            <span className={`material-symbols-outlined ${styles['home-icon']}`}>home</span>
          </Link>

          {effectiveCrumbs.map((crumb, index) => {
            const isLast = index === effectiveCrumbs.length - 1;

            return (
              <React.Fragment key={index}>
                <span className={styles['breadcrumb-sep']}>/</span>
                {isLast || !crumb.path ? (
                  <span
                    className={`${styles['breadcrumb-item']} ${isLast ? styles['breadcrumb-current'] : ''}`}
                  >
                    {crumb.label}
                  </span>
                ) : (
                  <Link to={crumb.path} className={styles['breadcrumb-item']}>
                    {crumb.label}
                  </Link>
                )}
              </React.Fragment>
            );
          })}
        </nav>
      </div>

      {/* Right section — action icons */}
      <div className={styles['topbar-right']}>
        <button className={styles['topbar-icon-btn']} aria-label="Notificações">
          <span className={`material-symbols-outlined ${styles['topbar-icon']}`}>notifications</span>
        </button>
        <Link to="/ajuda" className={styles['topbar-icon-btn']} aria-label="Ajuda" title="Ajuda">
          <span className={`material-symbols-outlined ${styles['topbar-icon']}`}>help</span>
        </Link>
      </div>
    </header>
  );
};

export default TopBar;
